import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Wrench, RefreshCw, RotateCcw, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { fmtR } from './constants';
import { DatePickerField } from '../DatePickerField';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reports > Cost Budget tab (also embedded on Compliance).
 * Rows now cover BOTH upcoming services AND licence renewals; a Type
 * column labels each row. Each cell (date + estimated cost) is editable
 * and saves via `PUT /api/services/forecast/override`, keyed on
 * (vehicle_id, entry_type) so services and licences override
 * independently.
 */
export const ServiceBudgetCard = ({ readonly = false, months = 3 }) => {
  const [loading, setLoading] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/services/forecast?months=${months}`);
      setForecast(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load cost forecast');
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => { load(); }, [load]);

  const rowKey = (item, bucket) => `${item.vehicle_id}_${item.entry_type || 'service'}_${bucket.key}`;

  // Row calls this with its OWN local `amount` + `date` state so we never
  // race a stale prop value against the user's in-flight edit (this used to
  // silently blank the amount if the user picked a date before blurring the
  // amount input).
  const saveOverride = async (bucket, item, { override_amount, override_date }) => {
    setSavingKey(rowKey(item, bucket));
    try {
      await axios.put(`${API_URL}/api/services/forecast/override`, {
        vehicle_id: item.vehicle_id,
        entry_type: item.entry_type || 'service',
        override_amount,
        override_date,
      });
      toast.success('Saved ✓', { duration: 1500 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingKey(null);
    }
  };

  const clearOverride = async (bucket, item) => {
    setSavingKey(rowKey(item, bucket));
    try {
      const et = item.entry_type || 'service';
      await axios.delete(`${API_URL}/api/services/forecast/override/${item.vehicle_id}?entry_type=${et}`);
      toast.success('Reset to auto-forecast');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading && !forecast) {
    return (
      <Card className="card-hover">
        <CardContent className="py-12 text-center text-slate-400">Loading cost forecast…</CardContent>
      </Card>
    );
  }

  const grandTotal = forecast?.grand_total || 0;
  const buckets = forecast?.months || [];

  return (
    <Card className="card-hover" data-testid="service-budget-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
            <Wrench className="w-5 h-5 text-slate-700" />
            Cost Budget — Next {months} Months
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Upcoming services and licence renewals per vehicle. Edit the date or amount to lock in a firmer budget; blanks default to R 0.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-sm" data-testid="grand-total-badge">
            3-mo total: {fmtR(grandTotal)}
          </Badge>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reload-forecast-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {buckets.map((bucket) => (
          <div key={bucket.key} data-testid={`bucket-${bucket.key}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-slate-800">{bucket.label}</h3>
              <div className="text-sm text-slate-600">
                Total <span className="font-semibold" data-testid={`bucket-total-${bucket.key}`}>{fmtR(bucket.total)}</span>
              </div>
            </div>

            {bucket.items.length === 0 ? (
              <div className="py-4 px-3 text-sm text-slate-400 bg-slate-50 rounded-md">
                No costs expected this month.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-left">
                    <tr>
                      <th className="px-3 py-2">Vehicle</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2 text-right">Current</th>
                      <th className="px-3 py-2 text-right">To Next Service</th>
                      <th className="px-3 py-2">Expected Date</th>
                      <th className="px-3 py-2 text-right">Estimated Cost (R)</th>
                      <th className="px-3 py-2 text-right w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {bucket.items.map((item) => (
                      <CostForecastRow
                        key={rowKey(item, bucket)}
                        bucket={bucket}
                        item={item}
                        readonly={readonly}
                        saving={savingKey === rowKey(item, bucket)}
                        onSave={saveOverride}
                        onClear={clearOverride}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const TypeBadge = ({ type }) => {
  const isLicence = type === 'licence';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        isLicence ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'bg-amber-50 text-amber-700 border border-amber-100'
      }`}
    >
      {isLicence ? <FileText className="w-3 h-3" /> : <Wrench className="w-3 h-3" />}
      {isLicence ? 'Licence' : 'Service'}
    </span>
  );
};

const CostForecastRow = ({ bucket, item, readonly, saving, onSave, onClear }) => {
  const entryType = item.entry_type || 'service';
  const baseCost = entryType === 'licence'
    ? (item.estimated_cost != null && item.override_amount == null ? item.estimated_cost : null)
    : item.last_service_cost;
  const placeholder = baseCost != null ? String(baseCost) : '0';

  const [date, setDate] = useState(item.override_date || item.projected_date || '');
  const [amount, setAmount] = useState(
    item.override_amount != null
      ? String(item.override_amount)
      : (baseCost != null ? String(baseCost) : '')
  );

  useEffect(() => {
    setDate(item.override_date || item.projected_date || '');
    setAmount(
      item.override_amount != null
        ? String(item.override_amount)
        : (baseCost != null ? String(baseCost) : '')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.override_date, item.projected_date, item.override_amount, item.last_service_cost, item.estimated_cost]);

  const hasOverride = item.override_amount != null || item.override_date != null;

  // Always fire with the CURRENT local state so a pending amount is never
  // overwritten when the user picks a new date (and vice versa).
  const flush = ({ nextDate = date, nextAmount = amount } = {}) => {
    const parsedAmount = nextAmount === '' || nextAmount === null ? null : parseFloat(nextAmount);
    const parsedDate = nextDate || null;
    // Skip the save if nothing changed compared to the current item.
    if (
      parsedAmount === (item.override_amount ?? null) &&
      parsedDate === (item.override_date ?? null)
    ) {
      return;
    }
    onSave(bucket, item, {
      override_amount: parsedAmount,
      override_date: parsedDate,
    });
  };

  return (
    <tr className="hover:bg-slate-50" data-testid={`forecast-row-${item.vehicle_id}-${entryType}-${bucket.key}`}>
      <td className="px-3 py-2">
        <div className="font-medium text-slate-800">{item.vehicle_name}</div>
        <div className="text-xs text-slate-500">{item.registration}</div>
      </td>
      <td className="px-3 py-2">
        <TypeBadge type={entryType} />
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700" data-testid={`current-reading-${item.vehicle_id}-${entryType}-${bucket.key}`}>
        {item.current_reading != null
          ? `${Number(item.current_reading).toLocaleString('en-ZA', { maximumFractionDigits: 0 })} ${item.meter_unit || 'km'}`
          : <span className="text-slate-400">—</span>}
      </td>
      <td className="px-3 py-2 text-right tabular-nums" data-testid={`to-service-${item.vehicle_id}-${entryType}-${bucket.key}`}>
        {entryType === 'service' && item.distance_to_service != null ? (
          item.distance_to_service > 0 ? (
            <span className={item.distance_to_service < 1000 ? 'text-amber-700 font-medium' : 'text-slate-700'}>
              {Number(item.distance_to_service).toLocaleString('en-ZA', { maximumFractionDigits: 0 })} {item.meter_unit || 'km'}
            </span>
          ) : (
            <span className="text-red-600 font-medium">
              Overdue {Number(Math.abs(item.distance_to_service)).toLocaleString('en-ZA', { maximumFractionDigits: 0 })} {item.meter_unit || 'km'}
            </span>
          )
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        {readonly ? (
          <span>{date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</span>
        ) : (
          <DatePickerField
            value={date || null}
            onChange={(iso) => {
              setDate(iso || '');
              flush({ nextDate: iso });
            }}
            disabled={saving}
            placeholder="Pick a date"
            className="w-44"
            triggerTestId={`override-date-${item.vehicle_id}-${entryType}-${bucket.key}`}
          />
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {readonly ? (
          <span className="tabular-nums">{item.estimated_cost != null ? fmtR(item.estimated_cost) : '—'}</span>
        ) : (
          <Input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={() => flush({ nextAmount: amount })}
            disabled={saving}
            className="w-32 h-8 ml-auto text-right tabular-nums"
            placeholder={placeholder}
            data-testid={`override-amount-${item.vehicle_id}-${entryType}-${bucket.key}`}
          />
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {hasOverride && !readonly ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onClear(bucket, item)}
            disabled={saving}
            title="Reset to auto-forecast"
            data-testid={`clear-override-${item.vehicle_id}-${entryType}-${bucket.key}`}
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        ) : (
          <span className="text-xs text-slate-400">auto</span>
        )}
      </td>
    </tr>
  );
};

export default ServiceBudgetCard;
